import { useEffect } from 'react';
import { PageContainer, Tile } from 'components/Layout/PageContainer';
import { Service, useProDataService } from 'services/data.service';
import { useDataRequest } from 'hooks/useDataRequest';
import { useDataServiceQueryResult } from 'hooks/useDataServiceQueryResult';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { useAppStore } from 'hooks/state/useAppStore';
import { MarketplaceChannelBreakdownInfoContent } from './MarketplaceChannelBreakdownInfoContent';
import { MarketplaceChannelBreakdownTable } from 'pages/insights-pro/marketplace-tab/reports/marketplace-channel-breakdown/MarketplaceChannelBreakdownTable';

export function MarketplaceChannelBreakdown() {
  const { setContent } = useInfoDrawer();

  const { fetchData } = useProDataService(Service.Marketplaces);
  const requestData = useDataRequest();

  useEffect(() => {
    setContent({
      titleKey: 'insights.marketplace.reports.channel_breakdown',
      subtitleKey: 'info.content.all_dates_utc',
      content: <MarketplaceChannelBreakdownInfoContent />,
    });

    return () => {
      setContent(null);
    };
  }, [setContent]);

  const { products } = useAppStore();

  const reportState = useDataServiceQueryResult(
    requestData,
    baseData =>
      fetchData('reports/channel-breakdown', {
        ...baseData,
        filters: {
          ...baseData.filters,
          productNumbers: products,
        },
      }),
    response => {
      const isComparisonActive = Boolean(
        requestData.comparisonTimeFilter?.fromDate && requestData.comparisonTimeFilter?.toDate
      );
      return {
        isComparisonActive,
        response,
      };
    },
    ['insights-marketplaces-channel-breakdown-report']
  );

  if (!reportState.data && !reportState.isError) {
    return <MetricsLoadingSpinner />;
  }

  if (reportState.isError) {
    return (
      <PageContainer>
        <Tile className="text-lg p-4" titleId="insights.marketplace.reports.channel_breakdown" />
        <Tile titleId="insights.marketplace.reports.channel_breakdown.description" errorDetails={reportState.error} />
      </PageContainer>
    );
  }

  const isComparisonActive = reportState.data?.isComparisonActive ?? false;

  return (
    <PageContainer>
      <Tile className="text-lg p-4" titleId="insights.marketplace.reports.channel_breakdown" />
      <MarketplaceChannelBreakdownTable response={reportState.data!.response} isComparisonActive={isComparisonActive} />
    </PageContainer>
  );
}
